/**
 * The contract texts a club concludes: AGB and the data processing agreement
 * (AVV, Art. 28 GDPR), plus the operator's data they quote.
 *
 * They live in core since the online sign-up (0024): the API sends both texts
 * in full with the contract confirmation (a storable copy in text form), the
 * site renders them on /agb/ and /avv/, and the web form links them — one
 * source, so the mail can never carry another wording than the page.
 * `packages/data/src/legal/texts.ts` re-exports everything here; the privacy
 * statement, the Impressum and the members' terms stay there.
 *
 * Owner's decisions of 29.10.2026: the contract is with the club only (B2B,
 * never consumers); members use the app for free and accept nothing in the
 * app — the club informs them (AGB 7). Drafted 29.10.2026, NOT reviewed by a
 * lawyer. Operator data only from OPERATOR (verbatim Impressum, texts.test.ts
 * pins it); price figures from PRICING, deletion periods from RETENTION — the
 * same sources the site and the privacy statement quote, so they cannot
 * disagree. A substantive change bumps `version`; a concluded contract
 * records the versions it was concluded under (contracts.*_version).
 */
import { PRICING } from './pricing';
import { RETENTION } from './retention';
import { LOGIN_CODE_MAX_ATTEMPTS, LOGIN_CODE_TTL_MINUTES } from './limits';
import { SIGNUP_PENDING_HOURS } from './signup';

export interface LegalSection {
  id: string;
  title: string;
  paragraphs?: readonly string[];
  items?: readonly { term: string; text: string }[];
  list?: readonly string[];
}

export interface ContractText {
  readonly title: string;
  readonly version: string;
  readonly intro: string;
  readonly sections: readonly LegalSection[];
}

export const OPERATOR = {
  name: 'D-VO Consulting UG (haftungsbeschränkt)',
  street: 'Schlehenweg 1',
  city: '76337 Waldbronn',
  country: 'Deutschland',
  representative: 'Daniel Maria Vollmer',
  register: 'HRB 756892, Amtsgericht Mannheim',
  vatId: 'DE459868768',
  phone: '+49 (0) 151 26457675',
  fax: '+49 (0) 7243 946046',
  email: 'datenschutz@d-vo.de',
} as const;

/** Support address for members and clubs (site, AGB, Nutzungsbedingungen); also gets every new contract. */
export const SUPPORT_EMAIL = 'support@d-vo.de';

export const SITE_URL = 'https://aussencheck.app';
export const APP_URL = 'https://app.aussencheck.app';
export const PRIVACY_URL = `${SITE_URL}/datenschutz/`;
export const TERMS_URL = `${SITE_URL}/nutzungsbedingungen/`;
export const PRICES_URL = `${SITE_URL}/preise/`;
export const AGB_URL = `${SITE_URL}/agb/`;
export const AVV_URL = `${SITE_URL}/avv/`;
export const SIGNUP_URL = `${APP_URL}/verein-anmelden`;

/**
 * After a contract ends (AGB 10, AVV 12): a club may ask for an export this
 * many days, and the operator deletes the club's data this many days after
 * the end. Not a job of db/retention.sql — offboarding is a step of the
 * runbook (docs/runbook.md, „Vertragsende“), done by the platform manager.
 */
export const CONTRACT_END = {
  exportRequestDays: 30,
  deletionDays: 90,
} as const;

/** Version of the members' terms (TERMS in packages/data/src/legal/texts.ts); a contract records it. */
export const TERMS_VERSION = '29.10.2026';

export const months = (n: number) =>
  n % 12 === 0 ? `${n / 12} ${n === 12 ? 'Jahr' : 'Jahre'}` : `${n} Monate`;

export const AGB: ContractText = {
  title: 'Allgemeine Geschäftsbedingungen für Vereine',
  version: '30.10.2026',
  intro: `Diese Bedingungen regeln den Vertrag zwischen einem Verein und der ${OPERATOR.name} über die Nutzung von Außencheck. Sie richten sich ausschließlich an Vereine, Luftsportgruppen und andere Organisationen, nicht an Verbraucher.`,
  sections: [
    {
      id: 'geltung',
      title: '1. Geltungsbereich',
      paragraphs: [
        `Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge über die Nutzung von Außencheck zwischen der ${OPERATOR.name}, ${OPERATOR.street}, ${OPERATOR.city} (im Folgenden „Betreiberin“), und ihren Kunden (im Folgenden „Verein“).`,
        'Das Angebot richtet sich ausschließlich an Vereine, Luftsportgruppen und andere Organisationen, nicht an Verbraucher im Sinne des § 13 BGB.',
        'Abweichende oder ergänzende Bedingungen des Vereins gelten nur, wenn die Betreiberin ihnen ausdrücklich in Textform zustimmt.',
        `Für die einzelnen Personen, die der Verein zur Nutzung einträgt, gelten zusätzlich die Nutzungsbedingungen unter ${TERMS_URL}.`,
      ],
    },
    {
      id: 'leistungen',
      title: '2. Leistungen',
      paragraphs: [
        'Die Betreiberin stellt dem Verein Außencheck als Software zur Nutzung über das Internet bereit (Software as a Service): die Web-App unter app.aussencheck.app sowie die Apps für iOS und Android, soweit sie im App Store bzw. bei Google Play erhältlich sind. Damit melden, sichten und dokumentieren die Mitglieder des Vereins Schäden an den Luftfahrzeugen des Vereins. Den Funktionsumfang beschreibt aussencheck.app zum Zeitpunkt des Vertragsschlusses.',
        'Außencheck wird in Microsoft Azure in der Europäischen Union betrieben; die Datenbank liegt in Frankfurt am Main.',
        `Eingeschlossen sind unbegrenzt viele Mitglieder, alle Funktionen, die Apps, Weiterentwicklungen (Updates), Sicherungen und Support per E-Mail in deutscher Sprache an ${SUPPORT_EMAIL}. Eine Einrichtungsgebühr fällt nicht an. Für die Mitglieder des Vereins ist die Nutzung kostenlos.`,
        'Eine bestimmte Verfügbarkeit ist nicht vereinbart. Die Betreiberin bemüht sich um einen möglichst störungsfreien Betrieb und kündigt Wartungsarbeiten nach Möglichkeit vorher an.',
        'Die Betreiberin darf Außencheck weiterentwickeln und ändern, soweit die wesentlichen Funktionen erhalten bleiben.',
        'Auf Wunsch richtet die Betreiberin für den Verein mehrere getrennte Bereiche ein, etwa einen je Abteilung, jeweils mit eigener Verwaltung, eigenen Mitgliedern und eigener Flotte. Sie gehören zu einem Vertrag.',
      ],
    },
    {
      id: 'vertragsschluss',
      title: '3. Vertragsschluss',
      paragraphs: [
        'Die Darstellung auf aussencheck.app ist kein bindendes Angebot.',
        `Der Verein kann den Vertrag online unter ${SIGNUP_URL} schließen. Er gibt dort seine Daten ein, prüft sie in einer Zusammenfassung, stimmt diesen Bedingungen und dem Vertrag zur Auftragsverarbeitung zu und gibt mit der Schaltfläche „Kostenpflichtig abschließen“ ein Angebot ab. Bis dahin kann er jede Eingabe korrigieren. Die Betreiberin schickt daraufhin einen Bestätigungscode an die angegebene E-Mail-Adresse; mit der Eingabe dieses Codes nimmt die Betreiberin das Angebot an, und der Vertrag ist geschlossen. Wird der Code nicht innerhalb von ${SIGNUP_PENDING_HOURS} Stunden eingegeben, erlischt das Angebot.`,
        'Unmittelbar nach Vertragsschluss erhält der Verein eine Vertragsbestätigung mit Vertragsnummer, den vereinbarten Preisen und dem Wortlaut dieser Bedingungen und des Vertrags zur Auftragsverarbeitung per E-Mail. Die Betreiberin speichert den Vertrag; die jeweils gültige Fassung der Bedingungen steht unter aussencheck.app.',
        'Alternativ übermittelt die Betreiberin dem Verein auf Anfrage ein Angebot in Textform; der Vertrag kommt dann zustande, wenn der Verein dieses Angebot in Textform annimmt, etwa per E-Mail.',
        'Die Person, die für den Verein handelt, versichert, dazu berechtigt zu sein. Vertragssprache ist Deutsch.',
      ],
    },
    {
      id: 'preise',
      title: '4. Preise und Zahlung',
      paragraphs: [
        `Es gilt die Preisliste unter ${PRICES_URL} in der Fassung zum Zeitpunkt des Vertragsschlusses, soweit nichts anderes vereinbart ist. Maßgeblich ist die Zahl der vereinseigenen Luftfahrzeuge, die der Verein in Außencheck eingetragen hat, jeweils am Ersten eines Monats; nutzt der Verein mehrere Bereiche, zählen die Luftfahrzeuge aller Bereiche gemeinsam, jedes Kennzeichen einmal, und die Höchstgebühr der Preisliste gilt für den Verein insgesamt. Die bei der Anmeldung angegebene Flotte dient nur der ersten Preisangabe.`,
        `Alle Preise sind Bruttopreise einschließlich der gesetzlichen Umsatzsteuer (derzeit ${PRICING.vatPercent} %).`,
        `Die Gebühr wird monatlich berechnet oder, wenn vereinbart, jährlich im Voraus. Bei jährlicher Zahlung im Voraus gewährt die Betreiberin einen Nachlass von ${PRICING.yearlyDiscountPercent} %.`,
        `Es gibt insgesamt ${PRICING.founderClubs} Pionierplätze. Solange Plätze frei sind, erhalten Vereine, die einen Vertrag schließen, einen Pionierplatz und damit einen Nachlass von ${PRICING.founderDiscountPercent} % auf die Gebühr, solange ihr Vertrag ohne Unterbrechung besteht; die Vertragsbestätigung nennt ihn. Beide Nachlässe gelten nebeneinander. Der Pionierrabatt ist nicht übertragbar.`,
        'Rechnungen werden in Textform gestellt und sind innerhalb von 14 Tagen nach Zugang ohne Abzug zu zahlen.',
      ],
    },
    {
      id: 'preisanpassung',
      title: '5. Preisanpassung',
      paragraphs: [
        `Die Betreiberin kann die Preise einmal je Vertragsjahr um höchstens ${PRICING.maxIncreasePercent} % erhöhen; verpflichtet ist sie dazu nicht. Eine Erhöhung wird zum Beginn des nächsten Vertragsjahres wirksam und ist dem Verein mindestens sechs Wochen vorher in Textform anzukündigen.`,
        'Im Fall einer Erhöhung kann der Verein den Vertrag bis zu ihrem Wirksamwerden mit Wirkung zu diesem Zeitpunkt in Textform kündigen. Auf dieses Recht weist die Betreiberin in der Ankündigung hin.',
      ],
    },
    {
      id: 'laufzeit',
      title: '6. Laufzeit und Kündigung',
      paragraphs: [
        'Der Vertrag beginnt mit dem Vertragsschluss oder zu dem im Angebot genannten Datum und läuft zunächst ein Vertragsjahr (zwölf Monate), auch bei monatlicher Zahlung. Er verlängert sich jeweils um ein weiteres Vertragsjahr, wenn er nicht mit einer Frist von einem Monat zum Ende des Vertragsjahres gekündigt wird.',
        'Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt. Kündigungen bedürfen der Textform.',
        'Die Betreiberin kann den Zugang des Vereins sperren, solange ein wichtiger Grund vorliegt, insbesondere bei einem erheblichen Zahlungsverzug nach erfolgloser Mahnung oder bei einem schwerwiegenden Verstoß gegen Abschnitt 7. Die Daten bleiben dabei erhalten.',
        'Ein Pionierrabatt endet mit jeder Kündigung des Vertrags. Ein späterer neuer Vertrag richtet sich nach der dann gültigen Preisliste.',
      ],
    },
    {
      id: 'pflichten',
      title: '7. Pflichten des Vereins',
      paragraphs: ['Der Verein ist verpflichtet,'],
      list: [
        'die Mitgliederliste in Außencheck aktuell zu halten und Personen, die nicht mehr berechtigt sind, zeitnah zu deaktivieren;',
        'nur Personen einzutragen, die zur Nutzung berechtigt sind, insbesondere seine Mitglieder und Personen, die für ihn tätig sind;',
        'die eingetragenen Personen über die Nutzungsbedingungen zu informieren und für ihre Einhaltung zu sorgen;',
        'nur Inhalte hochzuladen, an denen er die erforderlichen Rechte hat – insbesondere Zeichnungen und Ansichten von Luftfahrzeugen, Fotos und Logos – und die Betreiberin von Ansprüchen Dritter freizustellen, die auf einer von ihm zu vertretenden Verletzung dieser Pflicht beruhen;',
        'Zugänge vertraulich zu behandeln und die Betreiberin unverzüglich zu informieren, wenn er einen Missbrauch vermutet;',
        'Außencheck nicht missbräuchlich zu nutzen, insbesondere keine rechtswidrigen Inhalte einzustellen, nicht zu versuchen, auf Daten anderer Vereine zuzugreifen, und den Betrieb nicht zu stören;',
        'Änderungen seiner Vertragsdaten – Anschrift, Rechnungsanschrift, vertretungsberechtigte Person – in Textform mitzuteilen.',
      ],
    },
    {
      id: 'lufttuechtigkeit',
      title: '8. Lufttüchtigkeit',
      paragraphs: [
        'Außencheck dokumentiert Schäden und Hinweise an Luftfahrzeugen. Es bewertet sie nicht und trifft keine Aussage über die Lufttüchtigkeit eines Luftfahrzeugs.',
        'Die Verantwortung für die Lufttüchtigkeit bleibt bei der Technik des Vereins und bei der verantwortlichen Pilotin oder dem verantwortlichen Piloten. Außencheck ersetzt weder die Vorflugkontrolle noch Instandhaltung, Prüfungen oder die vorgeschriebene Dokumentation der Luftfahrzeuge.',
        'Eine Kennzeichnung als gesperrt in Außencheck ist ein Hinweis an die Mitglieder; Buchungen in anderen Systemen verhindert sie nicht.',
      ],
    },
    {
      id: 'datenschutz',
      title: '9. Datenschutz',
      paragraphs: [
        `Für die personenbezogenen Daten, die der Verein und seine Mitglieder in Außencheck verarbeiten, ist der Verein Verantwortlicher im Sinne der DSGVO; die Betreiberin verarbeitet sie in seinem Auftrag. Mit diesem Vertrag schließen die Parteien den Vertrag zur Auftragsverarbeitung nach Art. 28 DSGVO unter ${AVV_URL} (AVV); er ist Bestandteil dieses Vertrags und geht ihm in Fragen des Datenschutzes vor.`,
        `Für den Betrieb der Plattform selbst – Anmeldung, Sitzungen und Protokolle – sowie für die Vertragsdaten des Vereins ist die Betreiberin selbst verantwortlich. Einzelheiten nennt die Datenschutzerklärung unter ${PRIVACY_URL}.`,
      ],
    },
    {
      id: 'vertragsende',
      title: '10. Daten bei Vertragsende',
      paragraphs: [
        `Während der Laufzeit kann die Verwaltung des Vereins jederzeit selbst eine vollständige Kopie der Vereinsdaten als ZIP-Archiv erstellen. Bis ${CONTRACT_END.exportRequestDays} Tage nach Vertragsende stellt die Betreiberin auf Anfrage einen solchen Export bereit. Eine Exportdatei wird nach ${RETENTION.exportDays} Tagen automatisch gelöscht.`,
        'Mit dem Vertragsende ist der Verein für seine Mitglieder in Außencheck nicht mehr erreichbar. Personen, die auch in einem anderen Verein eingetragen sind, behalten ihren Zugang zu diesem.',
        `${CONTRACT_END.deletionDays} Tage nach Vertragsende löscht die Betreiberin die Daten des Vereins, wie im AVV vereinbart. In Sicherungskopien können sie bis zum Ablauf von deren Fristen enthalten bleiben (Wiederherstellung auf jeden Zeitpunkt der letzten ${RETENTION.pointInTimeRestoreDays} Tage, wöchentliche Sicherungen ${months(RETENTION.longTermBackupMonths)}, wöchentliche Exporte ${RETENTION.dumpDays} Tage), wie in der Datenschutzerklärung beschrieben.`,
      ],
    },
    {
      id: 'haftung',
      title: '11. Haftung',
      paragraphs: [
        'Die Betreiberin haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit, bei Verletzung des Lebens, des Körpers oder der Gesundheit sowie nach dem Produkthaftungsgesetz.',
        'Bei leichter Fahrlässigkeit haftet die Betreiberin nur für die Verletzung einer wesentlichen Vertragspflicht, also einer Pflicht, deren Erfüllung die ordnungsgemäße Durchführung des Vertrags überhaupt erst ermöglicht und auf deren Einhaltung der Verein regelmäßig vertrauen darf. In diesem Fall ist die Haftung auf den vertragstypischen, vorhersehbaren Schaden begrenzt, höchstens auf die Gebühren des laufenden Vertragsjahres. Im Übrigen ist die Haftung für leichte Fahrlässigkeit ausgeschlossen.',
        'Entscheidungen über die Lufttüchtigkeit eines Luftfahrzeugs und über die Durchführung eines Flugs trifft der Verein bzw. die verantwortliche Person (Abschnitt 8); dafür übernimmt die Betreiberin keine Haftung. Absatz 1 bleibt unberührt.',
        'Diese Regeln gelten auch für die persönliche Haftung der Mitarbeiter und Erfüllungsgehilfen der Betreiberin. Die Haftung für Datenschutzverstöße richtet sich nach dem AVV.',
      ],
    },
    {
      id: 'aenderungen',
      title: '12. Änderungen dieser Bedingungen',
      paragraphs: [
        'Die Betreiberin kann diese Bedingungen ändern, soweit die Änderung Leistungsumfang, Preise, Laufzeit und Kündigung nicht berührt. Sie kündigt eine Änderung mindestens sechs Wochen vor ihrem Wirksamwerden in Textform an. Widerspricht der Verein nicht bis zum Wirksamwerden in Textform, gilt die Änderung als angenommen; auf diese Folge und auf die Frist weist die Betreiberin in der Ankündigung hin. Widerspricht der Verein, gilt der Vertrag zu den bisherigen Bedingungen weiter.',
        'Preise ändern sich nur nach Abschnitt 5.',
      ],
    },
    {
      id: 'schluss',
      title: '13. Schlussbestimmungen',
      paragraphs: [
        'Es gilt das Recht der Bundesrepublik Deutschland.',
        'Erklärungen nach diesem Vertrag – insbesondere Angebot und Annahme, Ankündigungen, Widerspruch und Kündigung – bedürfen der Textform (§ 126b BGB); eine E-Mail genügt.',
        'Ist eine Bestimmung dieser Bedingungen unwirksam, bleibt der Vertrag im Übrigen wirksam; an die Stelle der unwirksamen Bestimmung tritt die gesetzliche Regelung.',
      ],
    },
  ],
};

/**
 * Vertrag zur Auftragsverarbeitung (Art. 28 GDPR). Drafted 29.10.2026, not
 * reviewed by a lawyer. Every technical statement describes the real system
 * (docs/spec.md 15, CLAUDE.md rules 5, 9, 15–17, 21, 22, 31, 33, 45, infra/)
 * and must stay true: texts.test.ts ties the periods to RETENTION and the
 * claims to the code that makes them true, like the privacy statement's. A
 * new processor, data store or region is a change of this text (and a
 * notice to every club, section 7).
 */
export const AVV: ContractText = {
  title: 'Vertrag zur Auftragsverarbeitung',
  version: '29.10.2026',
  intro: `Vertrag zur Auftragsverarbeitung nach Art. 28 Abs. 3 DSGVO zwischen dem Verein, der Außencheck nutzt (Verantwortlicher, im Folgenden „Verein“), und der ${OPERATOR.name}, ${OPERATOR.street}, ${OPERATOR.city} (Auftragsverarbeiterin, im Folgenden „Betreiberin“). Er ergänzt den Vertrag über die Nutzung von Außencheck nach den AGB für Vereine (Hauptvertrag).`,
  sections: [
    {
      id: 'gegenstand',
      title: '1. Gegenstand und Dauer',
      paragraphs: [
        'Die Betreiberin verarbeitet personenbezogene Daten im Auftrag des Vereins, indem sie ihm Außencheck nach dem Hauptvertrag bereitstellt: das Schadensregister für die Luftfahrzeuge des Vereins als Web-App und als App für iOS und Android.',
        `Dieser Vertrag gilt, solange der Hauptvertrag besteht, und darüber hinaus, solange die Betreiberin nach dessen Ende noch Daten des Vereins verarbeitet (Abschnitt 12).`,
      ],
    },
    {
      id: 'art-zweck',
      title: '2. Art und Zweck der Verarbeitung',
      paragraphs: [
        'Art der Verarbeitung: Erheben, Speichern, Ordnen, Anzeigen, Übermitteln an die berechtigten Personen des Vereins, Verkleinern von Fotos und Videos, Sichern, Exportieren und Löschen.',
        'Zweck: die gemeinsame Dokumentation von Schäden an den Luftfahrzeugen des Vereins – Meldung, Sichtung, Verlauf bis zur Reparatur –, Hinweise der Technik, die Verwaltung der Personen, die der Verein zur Nutzung einträgt, und deren Benachrichtigung.',
      ],
    },
    {
      id: 'daten',
      title: '3. Art der Daten und Kreis der Betroffenen',
      paragraphs: [
        'Betroffen sind die Personen, die der Verein in Außencheck einträgt – Mitglieder und Personen, die für ihn tätig sind –, sowie Personen, die auf Fotos oder in Videos zu sehen oder zu hören sind.',
        'Verarbeitet werden folgende Arten von Daten:',
      ],
      list: [
        'Personendaten: Name, E-Mail-Adresse und weitere E-Mail-Adressen, Rolle und Status im Verein, ob und wann eine Einladung verschickt wurde, freiwillig ein Profilbild;',
        'Schadensmeldungen: Luftfahrzeug, Position auf der Zeichnung, Kategorie, Titel, Beschreibung, Status, Begründungen, Rückfragen und Hinweise, jeweils mit Namen und Zeitpunkt;',
        'Fotos und Videos von Schäden, bei Videos einschließlich Ton;',
        'Hinweise der Technik zu einem Luftfahrzeug und Lesebestätigungen mit Person und Zeitpunkt;',
        'das Änderungsprotokoll: wer wann welchen Eintrag wie geändert hat, mit altem und neuem Stand;',
        'Einstellungen, Flotte und Stammdaten des Vereins einschließlich Logo und Kontaktangaben;',
        'Geräte-Token für Benachrichtigungen in den Apps, soweit eine Person Benachrichtigungen erlaubt.',
      ],
    },
    {
      id: 'eigene',
      title: '4. Was nicht im Auftrag verarbeitet wird',
      paragraphs: [
        'Für die Anmeldung (Einmalcodes, Sitzungen), das Anmeldeprotokoll, die technischen Betriebsprotokolle, den Schutz vor Missbrauch und die Vertragsdaten des Vereins ist die Betreiberin selbst verantwortlich. Diese Verarbeitung beschreibt die Datenschutzerklärung unter ' +
          `${PRIVACY_URL}.`,
      ],
    },
    {
      id: 'weisungen',
      title: '5. Weisungen',
      paragraphs: [
        'Die Betreiberin verarbeitet die Daten nur auf dokumentierte Weisung des Vereins, es sei denn, sie ist nach dem Recht der Union oder eines Mitgliedstaats dazu verpflichtet; in diesem Fall teilt sie dem Verein diese Anforderung vor der Verarbeitung mit, sofern das Recht dies nicht verbietet.',
        'Die Weisungen ergeben sich aus dem Hauptvertrag, diesem Vertrag und der Nutzung von Außencheck durch den Verein – etwa wenn seine Verwaltung Personen einträgt, deaktiviert oder anonymisiert, Einträge löscht oder einen Export erstellt. Weitere Weisungen erteilt der Verein in Textform an ' +
          `${OPERATOR.email}.`,
        'Ist die Betreiberin der Ansicht, dass eine Weisung gegen Datenschutzvorschriften verstößt, weist sie den Verein unverzüglich darauf hin.',
      ],
    },
    {
      id: 'vertraulichkeit',
      title: '6. Vertraulichkeit',
      paragraphs: [
        'Die Betreiberin setzt nur Personen ein, die zur Vertraulichkeit verpflichtet sind oder einer gesetzlichen Verschwiegenheitspflicht unterliegen. Zugriff auf die Daten eines Vereins erhalten sie nur, soweit es für den Betrieb, die Fehlerbehebung oder eine Weisung des Vereins erforderlich ist.',
        'Die Plattformverwaltung der Betreiberin sieht Stammdaten und Zahlen eines Vereins, nicht seine Schadensmeldungen, Fotos oder Mitgliederlisten. Einen Export der Vereinsdaten erstellt sie nur auf Weisung des Vereins oder nach Vertragsende (Abschnitt 12); jeder Export wird im Änderungsprotokoll des Vereins vermerkt.',
      ],
    },
    {
      id: 'unterauftrag',
      title: '7. Unterauftragsverarbeiter',
      paragraphs: [
        'Der Verein genehmigt die folgenden Unterauftragsverarbeiter:',
      ],
      items: [
        {
          term: 'Microsoft Ireland Operations Ltd., Dublin (Irland)',
          text: 'Microsoft Azure: Hosting, Datenbank (Region Germany West Central, Frankfurt am Main), Dateispeicher und Programmlogik (Region Westeuropa, Niederlande), Betriebsprotokolle, Sicherungen sowie der Versand von E-Mails über Azure Communication Services mit Datenstandort Europa.',
        },
        {
          term: 'Apple (Apple Push Notification service)',
          text: 'Nur für Personen, die in der App für iOS Benachrichtigungen erlauben: Zustellung der Benachrichtigung mit Geräte-Token und Inhalt der Nachricht (Kennzeichen, Titel des Schadens bzw. neuer Status, keine Namen oder E-Mail-Adressen). Eine Übermittlung in die USA ist möglich.',
        },
        {
          term: 'Google (Firebase Cloud Messaging)',
          text: 'Dasselbe für Personen, die in der App für Android Benachrichtigungen erlauben. Eine Übermittlung in die USA ist möglich.',
        },
      ],
    },
    {
      id: 'unterauftrag-aenderung',
      title: '8. Änderung von Unterauftragsverarbeitern',
      paragraphs: [
        'Die Betreiberin informiert den Verein in Textform mindestens vier Wochen im Voraus, bevor sie einen Unterauftragsverarbeiter hinzuzieht oder ersetzt. Der Verein kann innerhalb dieser Frist aus wichtigem datenschutzrechtlichem Grund in Textform widersprechen. Können sich die Parteien nicht einigen, kann der Verein den Hauptvertrag zum Wirksamwerden der Änderung kündigen.',
        'Die Betreiberin verpflichtet jeden Unterauftragsverarbeiter vertraglich auf dieselben Datenschutzpflichten wie in diesem Vertrag. Eine Übermittlung in ein Land außerhalb der EU und des EWR erfolgt nur, wenn die Voraussetzungen der Art. 44 ff. DSGVO erfüllt sind.',
      ],
    },
    {
      id: 'sicherheit',
      title: '9. Sicherheit der Verarbeitung',
      paragraphs: [
        'Die Betreiberin trifft die technischen und organisatorischen Maßnahmen nach Art. 32 DSGVO, die in der Anlage beschrieben sind. Sie darf sie an den Stand der Technik anpassen, solange das Schutzniveau nicht sinkt.',
      ],
    },
    {
      id: 'betroffenenrechte',
      title: '10. Unterstützung des Vereins',
      paragraphs: [
        'Die Betreiberin unterstützt den Verein dabei, die Rechte der Betroffenen zu wahren (Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit, Widerspruch). Vieles erledigt der Verein selbst in Außencheck: Personen bearbeiten, deaktivieren und anonymisieren, Einträge löschen, einen vollständigen Export erstellen. Wendet sich eine betroffene Person direkt an die Betreiberin, leitet sie das Anliegen unverzüglich an den Verein weiter.',
        'Sie unterstützt den Verein außerdem bei seinen Pflichten nach Art. 32 bis 36 DSGVO, soweit es ihr mit den ihr vorliegenden Informationen möglich ist.',
      ],
    },
    {
      id: 'verletzungen',
      title: '11. Meldung von Verletzungen',
      paragraphs: [
        'Die Betreiberin meldet dem Verein eine Verletzung des Schutzes der Daten des Vereins ohne schuldhaftes Zögern, nachdem sie ihr bekannt geworden ist, in Textform an die hinterlegten Kontaktadressen. Die Meldung enthält, soweit bekannt, Art der Verletzung, betroffene Kategorien und ungefähre Zahl der Betroffenen und Datensätze, wahrscheinliche Folgen und die ergriffenen oder vorgeschlagenen Maßnahmen. Informationen, die noch fehlen, reicht sie nach.',
      ],
    },
    {
      id: 'loeschung',
      title: '12. Löschung und Rückgabe nach Vertragsende',
      paragraphs: [
        `Bis ${CONTRACT_END.exportRequestDays} Tage nach dem Ende des Hauptvertrags stellt die Betreiberin dem Verein auf Anfrage einen vollständigen Export der Vereinsdaten als ZIP-Archiv bereit (Rückgabe). Die Exportdatei wird nach ${RETENTION.exportDays} Tagen automatisch gelöscht.`,
        `${CONTRACT_END.deletionDays} Tage nach dem Ende des Hauptvertrags löscht die Betreiberin die Daten des Vereins: Personen, die in keinem anderen Verein eingetragen sind, werden anonymisiert; Schadensmeldungen, Fotos, Videos, Hinweise und Einstellungen des Vereins werden gelöscht. Der Löschung entgegenstehende gesetzliche Aufbewahrungspflichten bleiben unberührt.`,
        `In Sicherungskopien bleiben die Daten bis zum Ablauf von deren Fristen enthalten: Wiederherstellung auf jeden Zeitpunkt der letzten ${RETENTION.pointInTimeRestoreDays} Tage, wöchentliche Sicherungen ${months(RETENTION.longTermBackupMonths)}, wöchentliche Exporte ${RETENTION.dumpDays} Tage, entfernte Dateien ${RETENTION.photoSoftDeleteDays} Tage. Sie werden nicht für andere Zwecke verwendet.`,
        'Auf Anfrage bestätigt die Betreiberin die Löschung in Textform.',
      ],
    },
    {
      id: 'nachweise',
      title: '13. Nachweise und Kontrollen',
      paragraphs: [
        'Die Betreiberin stellt dem Verein auf Anfrage die Informationen zur Verfügung, die zum Nachweis der Einhaltung dieses Vertrags erforderlich sind, insbesondere eine aktuelle Beschreibung der technischen und organisatorischen Maßnahmen.',
        'Der Verein kann die Einhaltung nach rechtzeitiger Ankündigung – in der Regel mindestens vier Wochen – zu den üblichen Geschäftszeiten und ohne Störung des Betriebs prüfen oder durch einen zur Verschwiegenheit verpflichteten Prüfer prüfen lassen. Für die Rechenzentren genügen die Nachweise und Zertifizierungen von Microsoft.',
      ],
    },
    {
      id: 'haftung',
      title: '14. Haftung',
      paragraphs: [
        'Für Schäden aus einem Verstoß gegen die DSGVO haften die Parteien gegenüber Betroffenen nach Art. 82 DSGVO. Im Verhältnis der Parteien zueinander gelten Art. 82 Abs. 4 und 5 DSGVO sowie im Übrigen die Haftungsregeln des Hauptvertrags.',
      ],
    },
    {
      id: 'schluss',
      title: '15. Schlussbestimmungen',
      paragraphs: [
        'Dieser Vertrag wird zusammen mit dem Hauptvertrag geschlossen – online mit der Bestätigung durch den Code oder in Textform – und endet mit ihm, vorbehaltlich Abschnitt 12. Änderungen bedürfen der Textform; für Änderungen durch die Betreiberin gilt Abschnitt 12 der AGB entsprechend.',
        'Bei Widersprüchen zwischen diesem Vertrag und dem Hauptvertrag geht dieser Vertrag in Fragen des Datenschutzes vor. Ist eine Bestimmung unwirksam, bleibt der Vertrag im Übrigen wirksam. Es gilt das Recht der Bundesrepublik Deutschland.',
      ],
    },
    {
      id: 'anlage-tom',
      title: 'Anlage: Technische und organisatorische Maßnahmen',
      items: [
        {
          term: 'Ort und Betrieb',
          text: 'Betrieb ausschließlich in Microsoft Azure in der EU: Datenbank in Frankfurt am Main (Germany West Central), Dateispeicher, Programmlogik und Betriebsprotokolle in Westeuropa (Niederlande), E-Mail-Versand mit Datenstandort Europa. Die Rechenzentren und ihre physische Sicherung verantwortet Microsoft.',
        },
        {
          term: 'Verschlüsselung',
          text: 'Alle Verbindungen laufen verschlüsselt über HTTPS (TLS). Datenbank und Dateispeicher sind im Ruhezustand durch die Azure-Plattform verschlüsselt.',
        },
        {
          term: 'Anmeldung ohne Passwort',
          text: `Personen melden sich mit einem sechsstelligen Einmalcode an, der an ihre E-Mail-Adresse geht, ${LOGIN_CODE_TTL_MINUTES} Minuten gilt und nach ${LOGIN_CODE_MAX_ATTEMPTS} Fehlversuchen verfällt; Anfragen sind je Adresse und je Aufrufer begrenzt. Codes und Sitzungen werden nur als nicht umkehrbare Prüfsumme gespeichert. Es gibt keine Passwörter und keine Zugangsdaten anderer Dienste.`,
        },
        {
          term: 'Zugriffskontrolle und Mandantentrennung',
          text: 'Jede Anfrage wird auf dem Server geprüft: angemeldet, aktive Mitgliedschaft im Verein, ausreichende Rolle (Mitglied, Technik, Verwaltung). Jede Datenbankabfrage auf Vereinsdaten ist auf den Verein beschränkt; automatische Tests schlagen fehl, wenn eine Abfrage diese Bedingung nicht enthält. Daten eines anderen Vereins sind für niemanden sichtbar.',
        },
        {
          term: 'Keine Schlüssel im Code',
          text: 'Die Serverkomponente greift auf Datenbank und Dateispeicher mit einer verwalteten Identität (Managed Identity) zu, ohne Verbindungszeichenfolgen oder Kontoschlüssel; der Zugriff mit Kontoschlüssel ist am Datenspeicher abgeschaltet. Fotos werden über Links freigegeben, die für genau eine Datei und kurze Zeit gelten (Lesen eine Stunde, Hochladen 15 Minuten, nur Anlegen).',
        },
        {
          term: 'Minimale Rechte',
          text: 'Der Datenbankbenutzer der Anwendung darf Vereinsdaten nicht physisch löschen; Löschen bedeutet Markieren mit Zeitpunkt und Person (Papierkorb). Änderungs- und Anmeldeprotokoll kann die Anwendung nur ergänzen, nicht ändern. Löschfristen setzt ein eigener wöchentlicher Lauf mit erhöhten Rechten durch, nur nach einer geprüften Sicherung.',
        },
        {
          term: 'Nachvollziehbarkeit',
          text: `Jede Änderung wird mit Person, Zeitpunkt, altem und neuem Stand im Änderungsprotokoll festgehalten (${months(RETENTION.changeEventsMonths)}); frühere Fassungen bleiben zusätzlich in einer Versionshistorie der Datenbank. Anmeldungen werden mit Ergebnis protokolliert, E-Mail- und IP-Adresse nur als Prüfsumme (${months(RETENTION.loginEventsMonths)}).`,
        },
        {
          term: 'Datensparsamkeit bei Fotos und Videos',
          text: 'Fotos und Videos werden auf dem Gerät verkleinert und von Metadaten befreit (Aufnahmeort, Kameradaten). Der Server prüft jede Datei anhand ihres Inhalts und nimmt keine an, die solche Daten noch enthält oder nicht dem erwarteten Format entspricht; Dateinamen vergibt der Server.',
        },
        {
          term: 'Verfügbarkeit und Sicherung',
          text: `Datenbank: Wiederherstellung auf jeden Zeitpunkt der letzten ${RETENTION.pointInTimeRestoreDays} Tage und wöchentliche Sicherungen über ${months(RETENTION.longTermBackupMonths)}. Zusätzlich ein wöchentlicher Export aller Tabellen in ein zweites Speicherkonto, der sofort zurückgelesen und geprüft wird (${RETENTION.dumpDays} Tage). Entfernte Dateien sind ${RETENTION.photoSoftDeleteDays} Tage wiederherstellbar; Speicherkonten und Datenbankserver sind gegen versehentliches Löschen gesperrt.`,
        },
        {
          term: 'Protokolle und Überwachung',
          text: `Betriebsprotokolle ohne IP-Adressen, ${RETENTION.operationalLogDays} Tage; Zugriffe auf den Dateispeicher werden zur Missbrauchserkennung protokolliert. Verfügbarkeit und Sicherungen werden automatisch überwacht.`,
        },
        {
          term: 'Entwicklung und Änderungen',
          text: 'Jede Änderung wird vor der Auslieferung automatisch gebaut und getestet, einschließlich Tests der Zugriffsregeln gegen eine echte Datenbank; ein fehlgeschlagener Test verhindert die Auslieferung. Sicherheitsrelevante Regeln werden durch eigene Tests geschützt.',
        },
        {
          term: 'Organisation',
          text: 'Administrativen Zugriff auf die Azure-Umgebung hat nur die Betreiberin. Anfragen von Betroffenen und Vorfälle werden dokumentiert. Die Maßnahmen werden bei jeder wesentlichen Änderung des Systems überprüft.',
        },
      ],
    },
  ],
};

/** A contract text as plain text, for the confirmation mail (a storable copy, § 126b BGB). */
export function contractPlainText(text: ContractText): string {
  const out: string[] = [text.title.toUpperCase(), `Fassung vom ${text.version}`, '', text.intro];
  for (const s of text.sections) {
    out.push('', s.title);
    for (const p of s.paragraphs ?? []) out.push(p);
    for (const it of s.items ?? []) out.push(`${it.term}: ${it.text}`);
    for (const l of s.list ?? []) out.push(`– ${l}`);
  }
  return out.join('\n');
}
